import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { EnterpriseContext } from "@/context/EnterpriseContext";
import { UserContext } from "@/context/UserContext";

const DynamicUploadFlowModal = dynamic(
  () => import("@/components/UploadFlowModal"),
  {
    ssr: false,
  }
);

const DynamicBxAgentWizard = dynamic(
  () => import("@/sections/BXAgentV2/components/Wizard"),
  {
    ssr: false,
  }
);

export interface OpenUploadModalOptions {
  /** Pre-select a specific entity type (e.g., 'yield', 'fuel', 'water') */
  preselectedEntity?: string | null;
}

interface UploadFlowModalContextType {
  /** Whether the upload modal is currently open */
  isOpen: boolean;
  /** Open the upload flow modal */
  openUploadModal: (options?: OpenUploadModalOptions) => void;
  /** Close the upload flow modal */
  closeUploadModal: () => void;
}

const UploadFlowModalContext = createContext<UploadFlowModalContextType>({
  isOpen: false,
  openUploadModal: () => {},
  closeUploadModal: () => {},
});

export const useUploadFlowModal = () => {
  const context = useContext(UploadFlowModalContext);
  if (!context) {
    throw new Error(
      "useUploadFlowModal must be used within UploadFlowModalProvider"
    );
  }
  return context;
};

interface UploadFlowModalProviderProps {
  children: React.ReactNode;
}

export const UploadFlowModalProvider = ({
  children,
}: UploadFlowModalProviderProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const enterpriseContext = useContext(EnterpriseContext);
  const featureToggles = enterpriseContext?.featureToggles;

  const [isOpen, setIsOpen] = useState(false);
  const [preselectedEntity, setPreselectedEntity] =
    useState<string | null>(null);
  const [isBxAgentWizardOpen, setIsBxAgentWizardOpen] = useState(false);

  // Get enterpriseId from router
  const enterpriseId = router.query.enterpriseId
    ? Number(router.query.enterpriseId)
    : 0;

  const openUploadModal = useCallback((options?: OpenUploadModalOptions) => {
    setPreselectedEntity(options?.preselectedEntity ?? null);
    setIsOpen(true);
  }, []);

  const closeUploadModal = useCallback(() => {
    setIsOpen(false);
    setPreselectedEntity(null);
    // Refresh onboarding progress after upload modal closes
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as unknown[];
        return key[0] === "onboardingProgress" && key[1] === enterpriseId;
      },
    });
  }, [queryClient, enterpriseId]);

  const openBxAgentWizard = useCallback(() => {
    setIsBxAgentWizardOpen(true);
  }, []);

  const closeBxAgentWizard = useCallback(() => {
    setIsBxAgentWizardOpen(false);
  }, []);

  const handleBxAgentWizardComplete = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      jobId: number,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      integrationSlug: string
    ) => {
      setIsBxAgentWizardOpen(false);
      // Navigate to the import-agent page to show the running job
      router.push(`/enterpriseDashboard/${enterpriseId}/import-agent`);
    },
    [enterpriseId, router]
  );

  const value = useMemo(
    () => ({
      isOpen,
      openUploadModal,
      closeUploadModal,
    }),
    [isOpen, openUploadModal, closeUploadModal]
  );

  return (
    <UploadFlowModalContext.Provider value={value}>
      {children}
      {enterpriseId > 0 && user?.id && (
        <>
          <DynamicUploadFlowModal
            open={isOpen}
            onClose={closeUploadModal}
            enterpriseId={enterpriseId}
            userId={user.id.toString()}
            preselectedEntity={preselectedEntity}
            featureToggles={featureToggles}
            onOpenBxAgentWizard={openBxAgentWizard}
          />
          <DynamicBxAgentWizard
            open={isBxAgentWizardOpen}
            onCancel={closeBxAgentWizard}
            onComplete={handleBxAgentWizardComplete}
          />
        </>
      )}
    </UploadFlowModalContext.Provider>
  );
};

export default UploadFlowModalContext;
